import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/Header';

interface NewTeamMember {
    name: string;
    position: string;
}

interface Photo {
    image_preview: string;
    image_file: File;
}

export async function uploadPhoto(file: File): Promise<string> {
    // ask backend for presigned POST url
    const r = await fetch(
        `/api/team/upload?name=${encodeURIComponent(file.name)}&content_type=${encodeURIComponent(file.type)}`,
        { method: 'POST', credentials: 'include' }
    );
    if (!r.ok) throw new Error('presign failed');
    const { url, fields, key } = await r.json();

    const form = new FormData();
    Object.entries(fields as Record<string, string>).forEach(([k, v]) =>
        form.append(k, v)
    );
    // fields first, file must be last
    form.append('file', file);

    // send to S3
    const s3Resp = await fetch(url, { method: 'POST', body: form });
    if (!s3Resp.ok) throw new Error('upload failed');
    return key;
}

export default function AddTeamMember() {
    const [photo, setPhoto] = useState<Photo>();
    const [teamMember, setTeamMember] = useState<NewTeamMember>({
        name: '',
        position: ''
    });
    const navigate = useNavigate();

    const handleSubmit = async () => {
        try {
            let photoKey = '';
            if (photo?.image_file)
                photoKey = await uploadPhoto(photo.image_file);

            const resp = await fetch('/api/team', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...teamMember, photo: photoKey })
            });

            if (resp.ok) navigate('/team', { replace: true });
            else alert('Error adding team member');
        } catch (e) {
            alert((e as Error).message);
        }
    };

    const handleImagePreview = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const files = e.target.files;
        if (files && files.length > 0) {
            const image_file = files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhoto({
                    image_preview: reader.result as string,
                    image_file: image_file
                });
            };
            reader.readAsDataURL(image_file);
        }
    };

    const handleChange = (name: keyof NewTeamMember, val: string) => {
        setTeamMember((prevPress) => ({
            ...prevPress,
            [name]: val
        }));
    };

    return (
        <div className="bg-black min-h-screen">
            <Header title="Add New Team Member" />
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                }}
                className="w-[80%] mx-auto py-10 text-white"
            >
                {photo && photo.image_preview && (
                    <img
                        src={photo.image_preview}
                        className="rounded-[30px] w-64 h-64"
                        alt="preview"
                    />
                )}

                <div className="mb-6">
                    <label
                        htmlFor="name"
                        className="block text-xl font-semibold"
                    >
                        Name
                    </label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={teamMember.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="w-full p-3 mt-2 bg-base-gray text-white rounded"
                        required
                    />
                </div>
                <div className="mb-6">
                    <label
                        htmlFor="position"
                        className="block text-xl font-semibold"
                    >
                        Position
                    </label>
                    <input
                        type="text"
                        id="position"
                        name="position"
                        value={teamMember.position}
                        onChange={(e) =>
                            handleChange('position', e.target.value)
                        }
                        className="w-full p-3 mt-2 bg-base-gray text-white rounded"
                        required
                    />
                </div>
                <div className="mb-6">
                    <label
                        htmlFor="link"
                        className="block text-xl font-semibold"
                    >
                        Select Photo
                    </label>
                    <input
                        type="file"
                        id="photo"
                        name="photo"
                        onChange={handleImagePreview}
                        className="w-full p-3 mt-2 bg-base-gray text-white rounded"
                        required
                    />
                </div>
                <div className="flex justify-between mt-10">
                    <button
                        type="submit"
                        className="px-5 py-2 bg-UL-green text-base-gray rounded-lg text-center text-xl font-normal"
                    >
                        Save
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/team', { replace: true })}
                        className="px-5 py-2 bg-gray-500 text-white rounded-lg text-center text-xl font-normal"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}
